import {
  CircleIcon,
  LineSegmentIcon,
  RectangleIcon,
  ShapesIcon,
  StarIcon,
  TextTIcon,
  TriangleIcon,
  XIcon,
} from '@phosphor-icons/react';
import type Konva from 'konva';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Layer, Line, Stage } from 'react-konva';
import { toast } from 'sonner';

import { EditModeShell } from '../EditModeShell';

import { KonvaLogoNode } from './KonvaLogoNode';
import { KonvaShapeNode } from './KonvaShapeNode';
import { KonvaTextNode } from './KonvaTextNode';
import { LogoPropertyBar } from './LogoPropertyBar';
import { LogoVariantPicker } from './LogoVariantPicker';
import { NativeTextarea } from './NativeTextarea';
import { ShapePropertyBar } from './ShapePropertyBar';
import { TextOverlayToolbar } from './TextOverlayToolbar';
import {
  type LogoElement,
  type ShapeElement,
  type TextElement,
  TEXT_PLACEHOLDER,
  getZIndex,
  isLogoElement,
  isPlaceholderText,
  isShapeElement,
  useTextEditorStore,
} from './useTextEditorStore';

import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import type { BrandKit } from '@/types/brand-kit';
import { logger } from '@/utils/logger';

interface MediaStudioTextOverlayProps {
  imageUrl: string;
  mediaId?: string;
  defaultFontFamily: string;
  onCancel: () => void;
  onSave: (blob: Blob) => Promise<void> | void;
  onLayersSave?: (
    layers: (TextElement | LogoElement | ShapeElement)[],
    stageWidth: number,
  ) => Promise<void> | void;
  isSaving?: boolean;
  initialLayers?: (TextElement | LogoElement | ShapeElement)[];
  brandKit?: BrandKit | null;
}

export function MediaStudioTextOverlay({
  imageUrl,
  defaultFontFamily,
  onCancel,
  onSave,
  onLayersSave,
  isSaving = false,
  initialLayers,
  brandKit,
}: MediaStudioTextOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageWrapRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const stageRef = useRef<Konva.Stage | null>(null);

  const [bgImage, setBgImage] = useState<HTMLImageElement | null>(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  // Pan drag state — middle-click or any-button drag on empty canvas area
  // when zoom > 1, like Figma. Refs avoid re-renders during drag.
  const panRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(
    null,
  );
  const [snapGuides, setSnapGuides] = useState<{ x: number | null; y: number | null }>({
    x: null,
    y: null,
  });
  const snapGuidesRef = useRef(snapGuides);
  const isMobile = useIsMobile();
  const SNAP_THRESHOLD = isMobile ? 8 : 5;

  const {
    elements,
    selectedId,
    addElement,
    selectElement,
    deleteElement,
    clearElements,
    loadElements,
    undo,
    redo,
  } = useTextEditorStore();

  // Load persisted layers when they arrive from the parent (async fetch).
  // Guard against re-hydrating after the user has started editing, otherwise
  // an unrelated re-render of `initialLayers` would clobber their changes.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (initialLayers && initialLayers.length > 0) {
      loadElements(initialLayers);
      hydratedRef.current = true;
    }
  }, [initialLayers, loadElements]);

  // Re-entrancy guard: prevent double-click on Done from firing two
  // concurrent saves before the parent's isSaving prop propagates.
  const isSavingRef = useRef(false);

  // Konva Stage size tracks the IMG's actual rendered bounding rect (per
  // https://konvajs.org/docs/sandbox/Responsive_Canvas.html). Observing the
  // IMG directly — not computing aspect from container size — avoids drift
  // when object-contain clamps the image to its parent in either dimension.
  useEffect(() => {
    if (!imgRef.current || !bgImage) return;
    const update = () => {
      const img = imgRef.current;
      if (!img) return;
      const rect = img.getBoundingClientRect();
      setStageSize({ width: rect.width, height: rect.height });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(imgRef.current);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [bgImage]);

  // Zoom-to-cursor (Figma/Canva pattern). Plain CSS `transform: scale(z)` zooms
  // around its transform-origin (center by default), so wheel always zoomed
  // into the same point regardless of cursor. Fix: when zoom changes, also
  // shift pan so the image-point that was under the cursor STAYS under the
  // cursor. Math:
  //   imagePoint = (cursor - pan) / oldZoom
  //   newPan = cursor - imagePoint * newZoom
  //         = pan * (newZoom/oldZoom) + cursor * (1 - newZoom/oldZoom)
  // The compensation is computed against the wrapper element's box (the
  // child of containerRef that carries the transform), so cursor coords are
  // measured relative to its untransformed origin.
  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const stageWrap = stageWrapRef.current;
    if (!stageWrap) {
      setZoom((z) => Math.min(Math.max(0.2, z - e.deltaY * 0.0015), 4));
      return;
    }
    // Cursor relative to the wrapper's centered (untransformed) origin.
    const rect = stageWrap.getBoundingClientRect();
    // rect already reflects the current transform; we want coordinates in the
    // wrapper's pre-transform space. Reverse-apply: divide by zoom and offset
    // by the wrapper's center.
    const cx = e.clientX - (rect.left + rect.width / 2);
    const cy = e.clientY - (rect.top + rect.height / 2);
    setZoom((oldZoom) => {
      const newZoom = Math.min(Math.max(0.2, oldZoom - e.deltaY * 0.0015), 4);
      if (newZoom === oldZoom) return oldZoom;
      const ratio = newZoom / oldZoom;
      setPan((p) => ({ x: p.x * ratio + cx * (1 - ratio), y: p.y * ratio + cy * (1 - ratio) }));
      return newZoom;
    });
  }, []);

  // Pan drag — when zoom > 1, the image overflows its container. Click+drag
  // anywhere on the canvas area (except on a Konva-listening element like a
  // text node) to pan. We rely on `stopPropagation` from KonvaTextNode's drag
  // handlers to prevent collisions; if the mousedown reaches our handler
  // here, the user clicked empty space (outside any text element).
  const handlePanStart = useCallback(
    (e: React.MouseEvent) => {
      if (zoom <= 1) return;
      // Don't start pan when clicking the textarea (inline editing) — let
      // text selection work. The textarea is the only HTML interactive
      // descendant; everything else is canvas pixels.
      const target = e.target as HTMLElement;
      if (target.closest('[contenteditable="true"], textarea, input')) return;
      panRef.current = { startX: e.clientX, startY: e.clientY, baseX: pan.x, baseY: pan.y };
      (e.currentTarget as HTMLElement).style.cursor = 'grabbing';
    },
    [zoom, pan.x, pan.y],
  );
  const handlePanMove = useCallback((e: React.MouseEvent) => {
    const ref = panRef.current;
    if (!ref) return;
    setPan({ x: ref.baseX + (e.clientX - ref.startX), y: ref.baseY + (e.clientY - ref.startY) });
  }, []);
  const handlePanEnd = useCallback(
    (e: React.MouseEvent) => {
      panRef.current = null;
      (e.currentTarget as HTMLElement).style.cursor = zoom > 1 ? 'grab' : '';
    },
    [zoom],
  );

  // Reset pan when zoom drops back to ≤1 (no overflow → no need to pan).
  useEffect(() => {
    if (zoom <= 1) setPan({ x: 0, y: 0 });
  }, [zoom]);

  // Block browser-level zoom (ctrl/meta + wheel) while text mode is mounted —
  // every ctrl-wheel inside the dialog should zoom the canvas, not the page.
  // Document-level passive: false so preventDefault sticks (passive listeners
  // can't preventDefault per the wheel-spec change in Chrome 73+).
  useEffect(() => {
    const blockBrowserZoom = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) e.preventDefault();
    };
    document.addEventListener('wheel', blockBrowserZoom, { passive: false });
    return () => document.removeEventListener('wheel', blockBrowserZoom);
  }, []);

  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    setBgImage(e.currentTarget);
  }, []);

  const handleElementDragMove = useCallback(
    (
      _elementId: string,
      x: number,
      y: number,
      width: number,
      height: number,
      node: { x: (v: number) => void; y: (v: number) => void },
    ) => {
      const centerX = x + width / 2;
      const centerY = y + height / 2;
      const midX = stageSize.width / 2;
      const midY = stageSize.height / 2;

      const newGuides = { x: null as number | null, y: null as number | null };

      if (Math.abs(centerX - midX) < SNAP_THRESHOLD) {
        newGuides.x = midX;
        // Imperatively set the Konva node position — avoids fighting
        // Konva's internal drag engine with a store write mid-drag.
        node.x(midX - width / 2);
      }
      if (Math.abs(centerY - midY) < SNAP_THRESHOLD) {
        newGuides.y = midY;
        node.y(midY - height / 2);
      }

      // Only update React state when guide values actually changed
      const prev = snapGuidesRef.current;
      if (prev.x !== newGuides.x || prev.y !== newGuides.y) {
        snapGuidesRef.current = newGuides;
        setSnapGuides(newGuides);
      }
    },
    [stageSize.width, stageSize.height, SNAP_THRESHOLD],
  );

  const handleElementDragEndGuides = useCallback(() => {
    snapGuidesRef.current = { x: null, y: null };
    setSnapGuides({ x: null, y: null });
  }, []);

  const handleAddText = useCallback(() => {
    const id = `text-${crypto.randomUUID()}`;
    addElement({
      id,
      text: TEXT_PLACEHOLDER,
      x: Math.max(0, stageSize.width / 2 - 100),
      y: Math.max(0, stageSize.height / 2 - 20),
      width: 200,
      fontSize: 32,
      fontFamily: defaultFontFamily,
      fontWeight: 400,
      isItalic: false,
      isUnderline: false,
      fill: '#ffffff',
      textAlign: 'left' as const,
      verticalAlign: 'top' as const,
      textTransform: 'none' as const,
      lineHeight: 1.2,
      letterSpacing: 0,
      contrastMode: 'none',
      contrastColor: '#000000',
      contrastIntensity: 90,
      contrastPadding: { top: 0, right: 0, bottom: 0, left: 0 },
      boxRoundness: 0,
      scaleX: 1,
      scaleY: 1,
      rotation: 0,
    });
    setEditingId(id);
  }, [stageSize, defaultFontFamily, addElement]);

  const handleAddLogo = useCallback(
    (variant: 'primary' | 'light' | 'dark', logoUrl: string) => {
      const addLogoElement = (w: number, h: number) => {
        const id = `logo-${crypto.randomUUID()}`;
        addElement({
          id,
          kind: 'logo',
          logoUrl,
          logoVariant: variant,
          x: (stageSize.width - w) / 2,
          y: (stageSize.height - h) / 2,
          width: w,
          height: h,
          rotation: 0,
          scaleX: 1,
          scaleY: 1,
          opacity: 1,
        } satisfies LogoElement);
        selectElement(id);
      };

      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const aspectRatio = img.naturalHeight / img.naturalWidth;
        const w = stageSize.width * 0.2;
        const h = w * aspectRatio;
        addLogoElement(w, h);
      };
      // Surface logo-load failures (CORS denied, 404, expired signature) so
      // they don't silently swallow the "Add logo" action. The element is
      // still added to the canvas at a default square size — KonvaLogoNode
      // renders its own dashed-placeholder + deduped-toast failure state for
      // it (BI-3-02), so a dead logo is never silently invisible. Without
      // this the popover closed but nothing appeared on the canvas at all —
      // confusing UX and an unreachable failure-state code path.
      img.onerror = () => {
        logger.error('MediaStudioTextOverlay: logo image failed to load', {
          variant,
          logoUrl,
        });
        const w = stageSize.width * 0.2;
        addLogoElement(w, w);
      };
      img.src = logoUrl;
    },
    [stageSize, addElement, selectElement],
  );

  const handleAddShape = (shapeType: ShapeElement['shapeType']) => {
    const id = crypto.randomUUID();
    const isLine = shapeType === 'line';
    const newShape: ShapeElement = {
      id,
      kind: 'shape',
      shapeType,
      x: (stageSize.width - 200) / 2,
      y: (stageSize.height - (isLine ? 4 : 200)) / 2,
      width: 200,
      height: isLine ? 4 : 200,
      rotation: 0,
      fill: '#3b82f6',
      opacity: 100,
      feather: 0,
      cornerRadius: shapeType === 'rectangle' ? 0 : undefined,
      points: shapeType === 'star' ? 5 : undefined,
      innerRadius: shapeType === 'star' ? 0.4 : undefined,
      strokeWidth: isLine ? 4 : undefined,
      strokeColor: isLine ? '#3b82f6' : undefined,
      zIndex: 0,
    };
    addElement(newShape);
  };

  const handleStageClick = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    // Click on empty stage = deselect (Konva: e.target === stage means the
    // hit-test missed every node and resolved to the stage itself).
    if (e.target === e.target.getStage()) {
      selectElement(null);
      setEditingId(null);
    }
  };

  const handleSave = async () => {
    if (isSavingRef.current) return; // Prevent double-click race
    isSavingRef.current = true;
    useTextEditorStore.getState().setIsSavingGlobal(true);

    try {
      // 1. Commit inline edits before saving. If the user clicks "Done"
      //    while typing, we need the final text in the store — not whatever
      //    partial state the blur handler leaves behind.
      if (editingId !== null) {
        setEditingId(null);
        selectElement(null);
      } else {
        selectElement(null); // Clear selection rings before export
      }

      // 2. Wait for all KonvaTextNode rasterizations to finish.
      //    Each node increments rasterizationPending before toPng and
      //    decrements after the Image loads. Poll with a 5s timeout cap.
      //    queueMicrotask lets React flush the setEditingId(null) update
      //    so rasterization effects actually start before we poll.
      // Wait a task boundary so React flushes the setEditingId(null) update
      // and KonvaTextNode rasterization effects start before we poll.
      await new Promise<void>((r) => setTimeout(r, 0));
      const waitStart = Date.now();
      await new Promise<void>((resolve) => {
        const check = () => {
          const pending = useTextEditorStore.getState().rasterizationPending;
          if (pending <= 0) {
            resolve();
            return;
          }
          if (Date.now() - waitStart > 5000) {
            logger.warn(
              `Text overlay save: rasterization timeout (${pending} still pending) — forcing save`,
            );
            useTextEditorStore.getState().resetRasterizationPending();
            resolve();
            return;
          }
          setTimeout(check, 50);
        };
        setTimeout(check, 50);
      });

      for (const el of useTextEditorStore.getState().elements) {
        if (!isLogoElement(el) && !isShapeElement(el) && isPlaceholderText(el.text)) {
          useTextEditorStore.getState().deleteElement(el.id);
        }
      }

      // Grab the latest elements after edits are committed
      const currentElements = useTextEditorStore.getState().elements;

      if (onLayersSave) {
        if (stageSize.width === 0) {
          logger.warn('Text overlay save: stageSize.width is 0 — aborting');
          return;
        }
        // Non-destructive: persist layers as metadata, don't composite
        await onLayersSave(currentElements, stageSize.width);
        clearElements();
        return;
      }

      // Legacy destructive: composite and save as image
      if (!stageRef.current || !bgImage) {
        logger.error('Text overlay save failed: stage or background image not available');
        return;
      }

      let targetW = bgImage.naturalWidth;
      let targetH = bgImage.naturalHeight;

      // Guard: cap canvas allocation to prevent OOM crash.
      // 4096x4096 = 16M pixels = ~64MB RGBA buffer — safe for all browsers.
      // Must run BEFORE toCanvas() so the text canvas also respects the cap.
      const MAX_EXPORT_PIXELS = 4096 * 4096;
      const pixels = targetW * targetH;
      if (pixels > MAX_EXPORT_PIXELS) {
        const capScale = Math.sqrt(MAX_EXPORT_PIXELS / pixels);
        targetW = Math.floor(targetW * capScale);
        targetH = Math.floor(targetH * capScale);
      }

      const baseScale = bgImage.naturalWidth / stageSize.width;
      const pixelRatio = Math.max(1, (targetW / bgImage.naturalWidth) * baseScale);

      const textCanvas = stageRef.current.toCanvas({ pixelRatio });

      const useOffscreen = typeof OffscreenCanvas !== 'undefined';
      const finalCanvas: OffscreenCanvas | HTMLCanvasElement = useOffscreen
        ? new OffscreenCanvas(targetW, targetH)
        : Object.assign(document.createElement('canvas'), { width: targetW, height: targetH });

      const ctx = finalCanvas.getContext('2d') as
        OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

      // Draw original image
      ctx.drawImage(bgImage, 0, 0, targetW, targetH);
      // Draw text overlay matching exactly
      ctx.drawImage(textCanvas, 0, 0, targetW, targetH);

      const blob = await new Promise<Blob | null>((resolve) => {
        if (useOffscreen) {
          (finalCanvas as OffscreenCanvas)
            .convertToBlob({ type: 'image/webp', quality: 0.9 })
            .then(resolve);
        } else {
          (finalCanvas as HTMLCanvasElement).toBlob((b) => resolve(b), 'image/webp', 0.9);
        }
      });

      if (blob) {
        await onSave(blob);
        clearElements();
      } else {
        logger.error('Text overlay save failed: could not generate composite blob');
        toast.error('Failed to save text — please try again.', { id: 'text-overlay-save-error' });
      }
    } catch (err) {
      logger.error('Failed to export text overlay', err);
      toast.error('Failed to save text — please try again.', { id: 'text-overlay-save-error' });
    } finally {
      isSavingRef.current = false;
      useTextEditorStore.getState().setIsSavingGlobal(false);
    }
  };

  const editingElement = elements.find((e) => e.id === editingId);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // CRITICAL: while inline-editing a text element, ALL keystrokes belong to
      // the TipTap editor. TipTap renders a `contenteditable` div (not <input> /
      // <textarea>), so the previous instance-of guard didn't catch it — Backspace
      // was deleting the whole element, arrow keys were nudging instead of moving
      // the cursor, ⌘Z was undoing the store. Bail here so the editor owns input.
      if (editingId !== null) return;
      const target = e.target as HTMLElement | null;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target && target.isContentEditable)
      ) {
        return;
      }

      const cmdOrCtrl = e.metaKey || e.ctrlKey;

      if (cmdOrCtrl && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      } else if (cmdOrCtrl && e.key === 'd' && selectedId) {
        e.preventDefault();
        const currentElements = useTextEditorStore.getState().elements;
        const selected = currentElements.find((el) => el.id === selectedId);
        if (selected) {
          const newId = `${isLogoElement(selected) ? 'logo' : isShapeElement(selected) ? 'shape' : 'text'}-${crypto.randomUUID()}`;
          addElement({
            ...selected,
            id: newId,
            x: selected.x + 20,
            y: selected.y + 20,
          });
          selectElement(newId);
        }
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        if (selectedId) {
          e.preventDefault();
          deleteElement(selectedId);
        }
      } else if (
        selectedId &&
        (e.key === 'ArrowLeft' ||
          e.key === 'ArrowRight' ||
          e.key === 'ArrowUp' ||
          e.key === 'ArrowDown')
      ) {
        // Arrow-key nudge: 1px default, 10px with Shift. Canva/Figma pattern.
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const currentElements = useTextEditorStore.getState().elements;
        const sel = currentElements.find((el) => el.id === selectedId);
        if (!sel) return;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        useTextEditorStore.getState().updateElement(selectedId, { x: sel.x + dx, y: sel.y + dy });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, editingId, addElement, selectElement, deleteElement, undo, redo]);

  // Disable EditModeShell's keyboard shortcuts while a text node is being
  // inline-edited (NativeTextarea active) so Esc cancels the inline edit
  // first instead of the whole mode.
  const isInlineEditing = editingId !== null;

  return (
    <div className="contents">
      <EditModeShell
        title="Editing text"
        onCancel={() => {
          clearElements();
          onCancel();
        }}
        onDone={handleSave}
        doneDisabled={isSaving || elements.length === 0}
        doneLoading={isSaving}
        doneTooltip={elements.length === 0 ? 'Add text, shape, or logo to start' : undefined}
        disableKeyboard={isInlineEditing}
        toolSlot={
          // overflow-x-auto + shrink-0/whitespace-nowrap on every trigger:
          // three buttons (Add text / Add logo / Add shape) plus Cancel/Done
          // in one h-11 row has no room to spare on a 375px viewport — text
          // was wrapping to 2 lines ("Add\ntext") instead of scrolling, which
          // read as broken (live review). All three now carry an icon + label
          // (Add logo already did; text/shape didn't, so the row looked
          // inconsistent) matching the entry-editor's own iconography.
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
            <button
              type="button"
              onClick={handleAddText}
              disabled={isSaving}
              aria-label="Add text"
              className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-caption font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            >
              <TextTIcon className="size-3.5" weight="regular" />
              Add text
            </button>
            <LogoVariantPicker
              brandKit={brandKit ?? null}
              onPick={handleAddLogo}
              disabled={isSaving}
            />
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={isSaving}
                  className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-caption font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ShapesIcon className="size-3.5" weight="regular" />
                  Add shape
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-2">
                <TooltipProvider delayDuration={300}>
                  <div className="grid grid-cols-3 gap-1">
                    {[
                      { type: 'rectangle' as const, icon: RectangleIcon, label: 'Rectangle' },
                      { type: 'circle' as const, icon: CircleIcon, label: 'Circle' },
                      { type: 'ellipse' as const, icon: CircleIcon, label: 'Ellipse' },
                      { type: 'star' as const, icon: StarIcon, label: 'Star' },
                      { type: 'triangle' as const, icon: TriangleIcon, label: 'Triangle' },
                      { type: 'line' as const, icon: LineSegmentIcon, label: 'Line' },
                    ].map(({ type, icon: Icon, label }) => (
                      <Tooltip key={type}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => handleAddShape(type)}
                            className="grid size-10 place-items-center rounded-md text-foreground/70 hover:bg-muted hover:text-foreground"
                          >
                            <Icon size={20} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{label}</TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </TooltipProvider>
              </PopoverContent>
            </Popover>
          </div>
        }
        propertyBar={(() => {
          if (!selectedId) {
            return (
              <p className="flex h-10 items-center px-4 text-detail text-muted-foreground/70">
                {elements.length === 0
                  ? 'Add text, shape, or logo to start'
                  : 'Click an element on the image to edit it'}
              </p>
            );
          }
          const sel = elements.find((el) => el.id === selectedId);
          if (sel && isLogoElement(sel)) return <LogoPropertyBar elementId={selectedId} />;
          if (sel && isShapeElement(sel)) return <ShapePropertyBar elementId={selectedId} />;
          return <TextOverlayToolbar stageSize={stageSize} />;
        })()}
        zoom={{ value: zoom, onChange: setZoom }}
      >
        {/* Wrapper matches the IDLE image positioning exactly: full-bleed flex
            box, image is `max-h-full max-w-full object-contain` centered. The
            old code used inline-flex with explicit stageSize width/height,
            which gave the image a different size + position than the idle
            state — that was the visible "jump" on mode entry. Now: same
            outer box, just an absolute Konva overlay anchored to the image. */}
        {/* Per Konva docs (https://konvajs.org/docs/sandbox/Responsive_Canvas.html):
            container must have explicit dimensions; Stage matches those. We let
            object-contain clamp the IMG to its parent (no inline width/height
            override that lets the IMG escape), then size the Stage to the IMG's
            actual rendered bounding rect via getBoundingClientRect on the IMG. */}
        <div
          ref={containerRef}
          onWheel={handleWheel}
          onMouseDown={handlePanStart}
          onMouseMove={handlePanMove}
          onMouseUp={handlePanEnd}
          onMouseLeave={handlePanEnd}
          className="relative flex min-h-0 flex-1 w-full items-center justify-center overflow-hidden"
          style={zoom > 1 ? { cursor: 'grab' } : undefined}
        >
          <div
            ref={stageWrapRef}
            className="relative flex max-h-full max-w-full items-center justify-center"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              // No transition during pan/zoom — the cursor-anchored math
              // depends on instantaneous frames, and a 75ms ease created
              // visible drift between cursor and image during scroll-zoom.
              transformOrigin: 'center',
            }}
          >
            <img
              ref={imgRef}
              src={imageUrl}
              alt="Text Overlay Base"
              crossOrigin="anonymous"
              onLoad={handleImageLoad}
              className="block max-h-full max-w-full object-contain"
            />

            {/* Konva Stage layered perfectly over the image.
                touch-none: without it, a single-finger drag on a text layer
                races the browser's native touch-scroll/pan on the ancestor
                divs, so drag-to-reposition was unreliable on real mobile
                devices. There is no touch pinch/pan gesture here to preserve
                (pan+zoom are wheel/mouse driven), so intercepting touch on the
                stage is safe. */}
            {stageSize.width > 0 && (
              <div className="absolute inset-0 z-10 touch-none">
                <Stage
                  ref={stageRef}
                  width={stageSize.width}
                  height={stageSize.height}
                  onMouseDown={handleStageClick}
                  onTouchStart={handleStageClick}
                >
                  <Layer>
                    {[...elements]
                      .sort((a, b) => getZIndex(a) - getZIndex(b))
                      .map((el) =>
                        isLogoElement(el) ? (
                          <KonvaLogoNode
                            key={el.id}
                            element={el}
                            isSelected={selectedId === el.id}
                            onDragMove={handleElementDragMove}
                            onDragEndGuides={handleElementDragEndGuides}
                          />
                        ) : isShapeElement(el) ? (
                          <KonvaShapeNode
                            key={el.id}
                            element={el}
                            isSelected={selectedId === el.id}
                            onDragMove={handleElementDragMove}
                            onDragEndGuides={handleElementDragEndGuides}
                          />
                        ) : (
                          <KonvaTextNode
                            key={el.id}
                            element={el}
                            isSelected={selectedId === el.id}
                            isEditing={editingId === el.id}
                            onDoubleClick={() => setEditingId(el.id)}
                            onDragMove={handleElementDragMove}
                            onDragEndGuides={handleElementDragEndGuides}
                          />
                        ),
                      )}
                  </Layer>
                  <Layer listening={false}>
                    {snapGuides.x !== null && (
                      <Line
                        points={[snapGuides.x, 0, snapGuides.x, stageSize.height]}
                        stroke="rgba(236, 72, 153, 0.8)"
                        strokeWidth={1}
                      />
                    )}
                    {snapGuides.y !== null && (
                      <Line
                        points={[0, snapGuides.y, stageSize.width, snapGuides.y]}
                        stroke="rgba(236, 72, 153, 0.8)"
                        strokeWidth={1}
                      />
                    )}
                  </Layer>
                </Stage>

                {/* Native DOM Textarea layered perfectly over Canvas */}
                {editingElement && (
                  <NativeTextarea element={editingElement} onClose={() => setEditingId(null)} />
                )}

                {/* Corner-X delete badge — Figma pattern. Positioned at the
                    selected element's top-right corner via Konva's bbox.
                    Konva's transformer handles the visual selection ring +
                    resize handles; we layer this DOM badge above. */}
                {selectedId &&
                  !isInlineEditing &&
                  (() => {
                    const sel = elements.find((el) => el.id === selectedId);
                    if (!sel) return null;
                    const right =
                      sel.x +
                      sel.width * ('scaleX' in sel ? (sel as { scaleX?: number }).scaleX || 1 : 1);
                    const top = sel.y;
                    return (
                      <CircleIconButton
                        size="sm"
                        onClick={() => deleteElement(selectedId)}
                        ariaLabel="Delete element"
                        tooltip="Delete (⌫)"
                        className="absolute z-20 -translate-x-1/2 -translate-y-1/2 bg-foreground text-background shadow-elevation-2 hover:bg-destructive"
                        style={{ left: `${right}px`, top: `${top}px` }}
                      >
                        <XIcon className="size-2.5" weight="bold" />
                      </CircleIconButton>
                    );
                  })()}
              </div>
            )}
          </div>
        </div>
      </EditModeShell>
    </div>
  );
}
